import { useQuery } from '@tanstack/react-query';

import {
  CorporateAboutUsPageViewModel,
  CorporateAgenciesPageViewModel,
  CorporateSeturYayinlariPageViewModel,
  SetairPageViewModel,
  SeturVizePageViewModel,
  StaticPagesViewModel,
} from '@api/index';
import { DocumentType } from '@api/v1';
import { callService } from '@api/remote-call';
import { useSsrData } from '@api/data-loaders/resource';
import { DocumentViewModel } from '@api/v3';

export type SideBarType = 'setur' | 'corporate';

export const useSideBarContentsQuery = ({ type }: { type: SideBarType }) => {
  return useQuery(
    ['sidebar', type],
    () =>
      callService(
        { service: 'ContentsService', method: 'getSidebar', version: 'v3' },
        { type }
      ),
    {
      cacheTime: Infinity,
      enabled: !!type,
    }
  );
};

export const useStaticPageQuery = ({ slug }: { slug: string }) => {
  const initialData = useSsrData() as StaticPagesViewModel;
  return useQuery(
    [slug],
    async () =>
      callService(
        { service: 'ContentsService', method: 'getStaticPages', version: 'v3' },
        { slug }
      ),
    {
      cacheTime: Infinity,
      enabled:
        !!slug &&
        ![
          'hakkimizda',
          'acentelerimiz',
          'setur-yayinlari',
          'kvkk',
          'cerez-politikasi',
        ].includes(slug),
      initialData,
    }
  );
};

export const useAboutUsQuery = () => {
  const initialData = useSsrData() as CorporateAboutUsPageViewModel;
  return useQuery(
    ['hakkimizda'],
    async () =>
      callService({
        service: 'ContentsService',
        method: 'getAboutUs',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};

export const useAgenciesQuery = () => {
  const initialData = useSsrData() as CorporateAgenciesPageViewModel;
  return useQuery(
    ['acentelerimiz'],
    async () =>
      callService({
        service: 'ContentsService',
        method: 'getAgencies',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};

export const useSeturPublicationsQuery = () => {
  const initialData = useSsrData() as CorporateSeturYayinlariPageViewModel;
  return useQuery(
    ['setur-yayinlari'],
    async () =>
      callService({
        service: 'ContentsService',
        method: 'getSeturYayinlari',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};

export const useSetairQuery = () => {
  const initialData = useSsrData() as SetairPageViewModel;
  return useQuery(
    ['setair'],
    async () =>
      callService({
        service: 'ContentsService',
        method: 'getSetair',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};

export const useSeturVizeQuery = () => {
  const initialData = useSsrData() as SeturVizePageViewModel;
  return useQuery(
    ['setur-vize'],
    async () =>
      callService({
        service: 'ContentsService',
        method: 'getSeturVize',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};

export const useOfficesQuery = () => {
  return useQuery(
    ['offices'],
    async () =>
      callService({
        service: 'MailsService',
        method: 'getOffices',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
    }
  );
};

export const useFormRequestTypesQuery = () => {
  return useQuery(
    ['offices'],
    async () =>
      callService({
        service: 'MailsService',
        method: 'getForms',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
    }
  );
};
export const useKVKKQuery = () => {
  const initialData = useSsrData() as DocumentViewModel;
  return useQuery(
    ['hakkimizda'],
    async () =>
      callService(
        {
          service: 'ContentsService',
          method: 'getDocument',
          version: 'v3',
        },
        { type: DocumentType.CONSENT_DOC }
      ),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};
export const useCookieQuery = () => {
  const initialData = useSsrData() as DocumentViewModel;
  return useQuery(
    ['hakkimizda'],
    async () =>
      callService(
        {
          service: 'ContentsService',
          method: 'getDocument',
          version: 'v3',
        },
        { type: DocumentType.COOKIE_TEXT }
      ),
    {
      cacheTime: Infinity,
      initialData,
    }
  );
};
